import {
	createContext,
	type UIEvent,
	useCallback,
	useContext,
	useRef,
	type WheelEvent,
} from "react";

export const SCROLLED_FROM_TOP_COLLAPSE_THRESHOLD_PX = 20;
export const SCROLLED_FROM_TOP_AT_TOP_THRESHOLD_PX = 1;
export const SCROLLED_FROM_TOP_EXPAND_WHEEL_PAUSE_MS = 150;
export const SCROLLED_FROM_TOP_COLLAPSE_TAIL_MS = 150;
/** How long downward scrolling is stopped after a collapse, matching the header's CSS
 *  collapse transition (duration-300). */
export const SCROLLED_FROM_TOP_COLLAPSE_STOP_MS = 300;

export type ReportScrolledFromTop = (scrolledFromTop: boolean) => void;

export type ScrolledFromTopReporting = {
	reportScrolledFromTop: ReportScrolledFromTop;
	/** Registers the scrollable element so the layout can forward wheel gestures from
	 *  chrome outside it (e.g. the account header) into it. Called with null on unmount. */
	registerScrollElement: (element: HTMLElement | null) => void;
	/** Reads the live header-collapsed state. When provided, the wheel gate intercepts the
	 *  first downward gesture at the top to collapse the header instead of scrolling content. */
	getHeaderCollapsed?: () => boolean;
};

export const ScrolledFromTopReporterContext = createContext<ScrolledFromTopReporting | null>(null);

export const useScrolledFromTopReporter = () => useContext(ScrolledFromTopReporterContext);

type WheelDelta = { deltaX: number; deltaY: number };

/** Ties break toward vertical, matching the collapse/expand direction check below — a
 *  perfectly diagonal gesture is treated as vertical. */
export const isVerticalWheel = (e: WheelDelta) => Math.abs(e.deltaY) >= Math.abs(e.deltaX);

/** What a wheel event should do to the header / native scroll. `block` means the native
 *  scroll must be prevented (only possible on a non-passive native listener). */
type WheelDecision = { collapse: boolean; expand: boolean; block: boolean };

const WHEEL_NONE: WheelDecision = { collapse: false, expand: false, block: false };
const WHEEL_EXPAND: WheelDecision = { collapse: false, expand: true, block: false };
const WHEEL_COLLAPSE_START: WheelDecision = { collapse: true, expand: false, block: true };
const WHEEL_COLLAPSE_STOP: WheelDecision = { collapse: false, expand: false, block: true };

/**
 * Wheel/scroll gesture handling for a scrollable region that drives the account header
 * collapse: collapses on the first downward wheel at the top, expands on an upward wheel
 * once settled there, and (opt-in via `getHeaderCollapsed`) briefly stops downward
 * scrolling right after a collapse so content doesn't move mid-animation.
 *
 * Settle/stop windows are fixed deadlines from the triggering instant, not a gap between
 * consecutive wheel events — a gap-based check never opens up during one long, smooth
 * scroll and would block indefinitely.
 *
 * Blocking only works on the native path (`attachNativeWheel`); React's `onWheel` is
 * passive, so `preventDefault` there is a no-op. Without `getHeaderCollapsed` the collapse
 * gate is off and `onScroll` (scrollTop past the threshold) is the only collapse trigger.
 * Reports are not deduplicated — the receiver ignores redundant ones.
 *
 * Pass `getScrollElement` when wheel events are captured on an element other than the
 * scrollable (e.g. surrounding chrome); the gesture then checks that element's scroll
 * position instead of the event's currentTarget.
 */
export const useScrolledFromTopGestures = (
	reportScrolledFromTop: ReportScrolledFromTop | null,
	getScrollElement?: () => HTMLElement | null,
	getHeaderCollapsed?: () => boolean,
) => {
	// Date.now() deadlines (not durations) for the post-collapse stop window and the
	// reversal's expand settle window.
	const collapseStopUntilRef = useRef(0);
	const collapseSettleAtRef = useRef(0);

	// Tracks the most recent arrival at the top, so expand gets its own settle deadline.
	// `wasAtTopRef` starts `null` (unobserved) rather than `false` so an already-at-top mount
	// isn't mistaken for a fresh arrival on the very first call.
	const wasAtTopRef = useRef<boolean | null>(null);
	const topSettleAtRef = useRef<number | null>(null);

	// Read inputs through refs so the returned callbacks stay referentially stable — the
	// native listener is attached via a callback ref, which would detach/reattach on every
	// render if its attach function changed identity.
	const reportRef = useRef(reportScrolledFromTop);
	const getScrollElementRef = useRef(getScrollElement);
	const getHeaderCollapsedRef = useRef(getHeaderCollapsed);
	reportRef.current = reportScrolledFromTop;
	getScrollElementRef.current = getScrollElement;
	getHeaderCollapsedRef.current = getHeaderCollapsed;

	const onScroll = (e: UIEvent<HTMLElement>) => {
		if (!reportRef.current) {
			return;
		}
		if (e.currentTarget.scrollTop > SCROLLED_FROM_TOP_COLLAPSE_THRESHOLD_PX) {
			reportRef.current(true);
		}
	};

	const decideWheel = useCallback(
		(scrollElement: HTMLElement | null, e: WheelDelta): WheelDecision => {
			if (!reportRef.current || !scrollElement) {
				return WHEEL_NONE;
			}
			const now = Date.now();

			const verticalDominant = isVerticalWheel(e);
			const wheelingDown = e.deltaY > 0 && verticalDominant;
			const wheelingUp = e.deltaY < 0 && verticalDominant;
			const atTop = scrollElement.scrollTop <= SCROLLED_FROM_TOP_AT_TOP_THRESHOLD_PX;

			const justArrivedAtTop = atTop && wasAtTopRef.current === false;
			wasAtTopRef.current = atTop;
			if (justArrivedAtTop) {
				topSettleAtRef.current = now + SCROLLED_FROM_TOP_EXPAND_WHEEL_PAUSE_MS;
			} else if (!atTop) {
				topSettleAtRef.current = null;
			}

			if (getHeaderCollapsedRef.current?.() === false) {
				if (wheelingDown && atTop) {
					collapseStopUntilRef.current = now + SCROLLED_FROM_TOP_COLLAPSE_STOP_MS;
					collapseSettleAtRef.current = now + SCROLLED_FROM_TOP_COLLAPSE_TAIL_MS;
					return WHEEL_COLLAPSE_START;
				}
				return WHEEL_NONE;
			}

			// Stop downward scrolling until the collapse animation finishes. Upward is left
			// alone here — gating it too can stick scroll-up indefinitely for a stationary
			// trackpad cursor, since it'd never see the pause a gap-based check would need.
			if (wheelingDown && now < collapseStopUntilRef.current) {
				return WHEEL_COLLAPSE_STOP;
			}

			// A reversal also waits out its own (shorter) settle window, so a quick down-then-up
			// doesn't read as an instant collapse/expand flicker.
			if (
				wheelingUp &&
				atTop &&
				(topSettleAtRef.current === null || now >= topSettleAtRef.current) &&
				now >= collapseSettleAtRef.current
			) {
				return WHEEL_EXPAND;
			}
			return WHEEL_NONE;
		},
		[],
	);

	const onWheel = (e: WheelEvent<HTMLElement>) => {
		const report = reportRef.current;
		if (!report) {
			return;
		}
		const scrollElement = getScrollElementRef.current
			? getScrollElementRef.current()
			: e.currentTarget;
		const decision = decideWheel(scrollElement, e);
		// React's onWheel is passive, so a collapse here can't block native scroll; that path
		// is handled by attachNativeWheel. Only expand is actioned on the React path.
		if (decision.expand) {
			report(false);
		}
	};

	const attachNativeWheel = useCallback(
		(element: HTMLElement) => {
			const handler = (e: globalThis.WheelEvent) => {
				const report = reportRef.current;
				if (!report) {
					return;
				}
				const decision = decideWheel(element, e);
				if (decision.block) {
					e.preventDefault();
				}
				if (decision.collapse) {
					report(true);
				} else if (decision.expand) {
					report(false);
				}
			};
			element.addEventListener("wheel", handler, { passive: false });
			return () => element.removeEventListener("wheel", handler);
		},
		[decideWheel],
	);

	return { onScroll, onWheel, decideWheel, attachNativeWheel };
};
